import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  IconButton,
  InputBase,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Button,
  Paper,
  Popover,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '../../../../assets/Admin/RequestImages/filter-icon.svg';
import ColoredFilterIcon from '../../../../assets/Admin/RequestImages/colored-filter-icon.svg';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import Layout from '../../../../components/Layout';
import AdminNavbar from '../../../../components/Admin/AdminNavbar/AdminNavbar';
import { axiosInstance } from '../../../../utils/tokenUtils';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import OneOnOneSessionIcon from '../../../../assets/Admin/SessionIcons/one-on-one-session-icon.svg';
import GroupSessionIcon from '../../../../assets/Admin/SessionIcons/group-session-icon.svg';
import PrimeSessionIcon from '../../../../assets/Admin/SessionIcons/prime-session-icon.svg';
import ReactCountryFlag from 'react-country-flag';
import { getCode as getCountryCodeByName } from 'country-list';
import './AddInstructor.css';

const AcademicLevelArray = [
  { value: 'All', label: 'All' },
  { value: 'elementary', label: 'Elementary' },
  { value: 'high_school', label: 'High School' },
  { value: 'middle_school', label: 'Middle School' },
  { value: 'university', label: 'University' },
];

const CreativeLevelArray = [
  { value: 'All', label: 'All' },
  { value: 'beginner', label: 'Beginner' },
  { value: 'intermediate', label: 'Intermediate' },
  { value: 'professional', label: 'Professional' },
];

const CategoryArray = [
  { value: 'All', label: 'All' },
  { value: 'teacher', label: 'Instructors' },
  { value: 'student_as_teacher', label: 'Learner As Instructor' },
];

// Only show pending, approved, and rejected statuses for requests
const StatusArray = [
  { value: 'All', label: 'All' },
  { value: 'approval_pending', label: 'Pending' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
];

const SessionTypeArray = [
  { value: 'All', label: 'All' },
  { value: 'one_on_one', label: 'One on One Session' },
  { value: 'group', label: 'Group Session' },
  { value: 'prime', label: 'Prime Session' },
];

const GenderArray = [
  { value: 'All', label: 'All' },
  { value: 'male', label: 'Male' },
  { value: 'female', label: 'Female' },
];

// Generate experience array (1-10 + More than 10)
const ExperienceArray = [
  { value: 'All', label: 'All' },
  { value: '1-3', label: '1-3 years' },
  { value: '4-6', label: '4-6 years' },
  { value: '7-9', label: '7-9 years' },
  { value: '10+', label: '10+ years' },
];

// Merge academic and creative levels (unique by value)
const LevelOptions = Array.from(
  new Map([...AcademicLevelArray, ...CreativeLevelArray].map(o => [o.value, o]))
).map(([, option]) => option);

function PopoverSubjects({ subjects }) {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl) && subjects.length > 1;

  const handlePopoverOpen = event => {
    if (subjects.length > 1) setAnchorEl(event.currentTarget);
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
  };

  // Extract subject titles for display (object or string)
  const subjectTitles = subjects.map(s => (typeof s === 'object' && s?.title ? s.title : s));

  return (
    <div
      onMouseEnter={handlePopoverOpen}
      onMouseLeave={handlePopoverClose}
      className="flex items-center cursor-pointer w-full"
    >
      <Box className="flex items-center" sx={{ gap: '6px' }}>
        <Typography className="subject-text">
          {subjectTitles && subjectTitles.length > 0 ? subjectTitles.slice(0, 1).join(', ') : '---'}
        </Typography>
        {subjects.length > 1 && (
          <Box className="request-subject-count-badge">
            <Typography className="request-subject-count-text">+{subjects.length - 1}</Typography>
          </Box>
        )}
      </Box>

      {subjects.length > 1 && (
        <Popover
          id={`popover-subjects`}
          sx={{
            pointerEvents: 'none',
            '& .MuiPopover-paper': {
              overflow: 'visible',
              borderRadius: '8px',
              boxShadow: '0px 4px 15px rgba(0, 0, 0, 0.1)',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: '-6px',
                left: '20px',
                width: '12px',
                height: '12px',
                backgroundColor: 'white',
                transform: 'rotate(45deg)',
                borderLeft: '1px solid rgba(0, 0, 0, 0.08)',
                borderTop: '1px solid rgba(0, 0, 0, 0.08)',
              },
            },
          }}
          open={open}
          anchorEl={anchorEl}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          onClose={handlePopoverClose}
          disableRestoreFocus
          PaperProps={{
            onMouseEnter: handlePopoverOpen,
            onMouseLeave: handlePopoverClose,
            sx: { pointerEvents: 'auto', width: '250px' },
          }}
        >
          <Box
            sx={{
              p: 1,
              '& > :not(:last-child)': { borderBottom: '1px solid rgba(0, 0, 0, 0.08)' },
            }}
          >
            {subjectTitles.map((subject, index) => (
              <Typography
                key={index}
                sx={{
                  py: 1,
                  px: 2,
                  fontSize: '12px',
                  fontFamily: 'Poppins',
                  '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)', borderRadius: '4px' },
                }}
              >
                {subject}
              </Typography>
            ))}
          </Box>
        </Popover>
      )}
    </div>
  );
}

const AddInstructor = () => {
  const navigate = useNavigate();
  const { id: orgId } = useParams();

  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState([]);
  const [instructors, setInstructors] = useState([]);
  const [apiLoading, setApiLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filterOpen, setFilterOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 30;
  const [hasMore, setHasMore] = useState(true);
  const tableContainerRef = React.useRef(null);
  const [filters, setFilters] = useState({
    level: 'All',
    category: 'All',
    years: 'All',
    language: 'All',
    country: 'All',
    city: 'All',
    gender: 'All',
  });

  // Pending filter states - will be applied when user clicks Apply
  const [pendingFilters, setPendingFilters] = useState({
    level: 'All',
    category: 'All',
    years: 'All',
    language: 'All',
    country: 'All',
    city: 'All',
    gender: 'All',
  });

  // Dynamic lists
  const [countries, setCountries] = useState([]);
  const [cities, setCities] = useState([]);
  const [languages, setLanguages] = useState([]);
  const [selectedCountryId, setSelectedCountryId] = useState(null);

  const handleFilterIconClick = event => {
    setAnchorEl(event.currentTarget);
    setFilterOpen(true);
    // Initialize pending filters with current active filters
    setPendingFilters({ ...filters });
  };

  const handleFilterClose = () => {
    setAnchorEl(null);
    setFilterOpen(false);
  };

  const handleFilterChange = (key, value) => {
    setPendingFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleClearAll = () => {
    setPendingFilters({
      level: 'All',
      category: 'All',
      years: 'All',
      language: 'All',
      country: 'All',
      city: 'All',
      gender: 'All',
    });
  };

  const handleApply = () => {
    const nextFilters = { ...pendingFilters };
    setFilters(nextFilters);
    setFilterOpen(false);
    setAnchorEl(null);
    // Fetch data with new filters (server-side)
    setPage(1);
    setHasMore(true);
    fetchInstructors(nextFilters, { pageArg: 1, append: false });
  };

  // Fetch instructors from API
  const fetchInstructors = async (activeFilters = filters, { pageArg, append } = {}) => {
    try {
      if (append) {
        setLoadingMore(true);
      } else {
        setApiLoading(true);
      }
      setError(null);
      // Build server-side filters
      const params = new URLSearchParams();
      // Required flag
      params.append('not_in_organisation', 'True');
      params.append('approval_status', 'approved');
      const currentPage = pageArg || 1;
      params.append('page', String(currentPage));
      params.append('page_size', String(PAGE_SIZE));
      if (search && search.trim()) {
        params.append('query', search.trim());
      }
      // Map UI filters to API params if not 'All' and not empty
      if (activeFilters?.level && activeFilters.level !== 'All') {
        params.append('level_to_teach', activeFilters.level);
      }
      if (activeFilters?.category && activeFilters.category !== 'All') {
        params.append('category', activeFilters.category);
      }
      if (activeFilters?.years && activeFilters.years !== 'All') {
        params.append('years_of_experience', activeFilters.years);
      }
      if (activeFilters?.language && activeFilters.language !== 'All') {
        params.append('teaching_language', activeFilters.language);
      }
      if (activeFilters?.city && activeFilters.city !== 'All') {
        params.append('city', activeFilters.city);
      }
      if (activeFilters?.country && activeFilters.country !== 'All') {
        params.append('country', activeFilters.country);
      }
      if (activeFilters?.gender && activeFilters.gender !== 'All') {
        params.append('gender', activeFilters.gender);
      }

      const response = await axiosInstance.get(`/admin/teachers?${params.toString()}`);

      // Transform the API response to match the expected format
      const transformedInstructors = response.data.results.map(instructor => ({
        id: instructor.id,
        first_name: instructor.first_name,
        last_name: instructor.last_name,
        profile_image: instructor.profile_image,
        subjects: instructor.subjects?.map(subject => subject.title) || ['N/A'],
        years_of_experience: instructor.years_of_experience,
        onboarded_date: new Date(instructor.registration_date).toLocaleDateString(),
        email: instructor.email,
        phone_no: instructor.phone_no,
        current_location: instructor.current_location,
        highest_qualification: instructor.highest_qualification,
        degree_name: instructor.degree_name,
        institute: instructor.institute,
        level_of_teaching: instructor.level_of_teaching,
        teaching_lang: instructor.teaching_lang,
        rating: instructor.rating || 0,
        total_sessions: instructor.total_sessions || 0,
        total_hours_taught: instructor.total_hours_taught,
        instructor_type: instructor.teacher_type
          ? instructor.teacher_type.charAt(0).toUpperCase() + instructor.teacher_type.slice(1)
          : 'Academic',
        country: instructor.current_location || 'N/A',
        allow_one_on_one_session: instructor.allow_one_on_one_session,
        allow_group_session: instructor.allow_group_session,
        allow_prime_session: instructor.allow_prime_session,
        is_active: instructor.is_active,
      }));

      if (append) {
        setInstructors(prev => [...prev, ...transformedInstructors]);
      } else {
        setInstructors(transformedInstructors);
      }
      // Determine if more pages exist
      const hasNext =
        typeof response.data.next !== 'undefined'
          ? Boolean(response.data.next)
          : transformedInstructors.length === PAGE_SIZE;
      setHasMore(hasNext);
      if (append) setPage(currentPage);
    } catch (error) {
      console.error('Error fetching instructors:', error);
      setError('Failed to load instructors. Please try again.');
      if (!append) setInstructors([]);
    } finally {
      if (append) {
        setLoadingMore(false);
      } else {
        setApiLoading(false);
      }
    }
  };

  // Fetch instructors when component mounts
  useEffect(() => {
    fetchInstructors(filters, { pageArg: 1, append: false });
  }, []);

  // Debounced server-side search
  useEffect(() => {
    const t = setTimeout(() => {
      setPage(1);
      setHasMore(true);
      fetchInstructors(filters, { pageArg: 1, append: false });
    }, 500);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  // Infinite scroll handler
  useEffect(() => {
    const el = tableContainerRef.current;
    if (!el) return;
    const onScroll = () => {
      if (loadingMore || apiLoading || !hasMore) return;
      if (el.scrollTop + el.clientHeight >= el.scrollHeight - 100) {
        const nextPage = page + 1;
        setPage(nextPage);
        fetchInstructors(filters, { pageArg: nextPage, append: true });
      }
    };
    el.addEventListener('scroll', onScroll);
    return () => el.removeEventListener('scroll', onScroll);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, hasMore, loadingMore, apiLoading, filters]);

  // Load countries and languages on mount
  useEffect(() => {
    const loadMeta = async () => {
      try {
        const [countriesRes, languagesRes] = await Promise.all([
          axiosInstance.get('/auth/countries'),
          axiosInstance.get('/auth/languages'),
        ]);
        setCountries(Array.isArray(countriesRes.data) ? countriesRes.data : []);
        setLanguages(Array.isArray(languagesRes.data) ? languagesRes.data : []);
      } catch (e) {
        console.error('Failed to load countries/languages', e);
        setCountries([]);
        setLanguages([]);
      }
    };
    loadMeta();
  }, []);

  // Load cities when a country selection changes in pending filters
  const fetchCitiesForCountry = async countryId => {
    if (!countryId) {
      setCities([]);
      return;
    }
    try {
      const res = await axiosInstance.get(`/auth/countries/${countryId}/cities`);
      setCities(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      console.error('Failed to load cities', e);
      setCities([]);
    }
  };

  const handleSearchChange = e => setSearch(e.target.value);

  const handleSelectAll = e => {
    if (e.target.checked) {
      setSelected(instructors.map(i => i.id));
    } else {
      setSelected([]);
    }
  };

  const handleSelect = id => {
    setSelected(prev => (prev.includes(id) ? prev.filter(sid => sid !== id) : [...prev, id]));
  };

  const handleConfirm = async () => {
    if (!orgId) {
      toast.error('Organization ID is missing.');
      return;
    }

    if (selected.length === 0) {
      toast.error('Please select at least one instructor to add.');
      return;
    }

    try {
      setLoading(true);

      // Make API call to add instructors to organization
      await axiosInstance.post(`/admin/organisation/add-teachers/${orgId}`, {
        teacher_ids: selected,
      });

      toast.success(`Successfully added ${selected.length} instructor(s) to the organization.`);

      // Navigate back to organization page
      navigate(`/admin/organisation/${orgId}`);
    } catch (error) {
      console.error('Error adding instructors to organization:', error);

      // Handle different error scenarios
      if (error.response) {
        const status = error.response.status;
        const errorMessage = error.response.data?.detail;

        if (status === 400) {
          toast.error(errorMessage);
        } else if (status === 401 || status === 403) {
          toast.error('You are not authorized to perform this action.');
        } else if (status === 404) {
          toast.error('Organization not found.');
        } else if (status === 409) {
          toast.error('Some instructors are already part of this organization.');
        } else {
          toast.error(`Error: ${errorMessage}`);
        }
      } else if (error.request) {
        toast.error('Network error. Please check your internet connection.');
      } else {
        toast.error('An unexpected error occurred. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  // We use server-side search (query param), so render the list directly

  return (
    <Layout title="Klass Online | Add Instructor">
      <Box className="relative w-full">
        <Box className="sticky top-0 left-0 right-0 z-[1000] bg-white pr-[10px]">
          <AdminNavbar />
        </Box>

        {/* Add Instructor Page Content */}
        <Box className="klass-add-instructor-page-container">
          {/* Header */}
          <Box className="klass-add-instructor-page-header">
            <Box className="klass-add-instructor-page-header-left">
              <Typography className="klass-add-instructor-page-title">Add Instructors</Typography>
            </Box>
            <Box className="klass-add-instructor-page-header-right">
              <Box className="klass-add-instructor-page-search-area">
                <Box className="klass-add-instructor-page-search">
                  <SearchIcon className="klass-add-instructor-search-icon" />
                  <InputBase
                    placeholder="Search by Name"
                    value={search}
                    onChange={handleSearchChange}
                    className="klass-add-instructor-search-input"
                    fullWidth
                    inputProps={{ 'aria-label': 'search instructors' }}
                  />
                </Box>
                {Object.values(filters).some(value => value !== 'All') ? (
                  <img
                    src={ColoredFilterIcon}
                    alt="Filter"
                    className="klass-add-instructor-filter-icon"
                    onClick={handleFilterIconClick}
                  />
                ) : (
                  <img
                    src={FilterListIcon}
                    alt="Filter"
                    className="klass-add-instructor-filter-icon"
                    onClick={handleFilterIconClick}
                  />
                )}
              </Box>
            </Box>
          </Box>

          {/* Filter Modal */}
          <Popover
            open={filterOpen}
            anchorEl={anchorEl}
            onClose={handleFilterClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            PaperProps={{
              className: 'klass-filter-modal-container',
            }}
          >
            {/* Header */}
            <Box className="klass-filter-modal-header-new">
              <Typography className="klass-filter-modal-title">Filters</Typography>
            </Box>

            {/* Scrollable Content */}
            <Box className="klass-filter-modal-content-new">
              {/* Grid for filter items */}
              <Box className="klass-filter-grid">
                {/* Level to Teach Filter */}
                <Box className="klass-filter-item">
                  <label className="klass-filter-label">Level to Teach</label>
                  <FormControl sx={{ width: '100%' }}>
                    <Select
                      value={pendingFilters.level}
                      onChange={e => handleFilterChange('level', e.target.value)}
                      displayEmpty
                      className="klass-filter-input"
                      IconComponent={KeyboardArrowDownIcon}
                      MenuProps={{
                        PaperProps: {
                          style: {
                            zIndex: 1500,
                          },
                        },
                      }}
                      sx={{
                        '& .MuiOutlinedInput-notchedOutline': {
                          border: 'none',
                        },
                      }}
                    >
                      {LevelOptions.map(opt => (
                        <MenuItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>

                {/* Category Filter */}
                <Box className="klass-filter-item">
                  <label className="klass-filter-label">Category</label>
                  <FormControl sx={{ width: '100%' }}>
                    <Select
                      value={pendingFilters.category}
                      onChange={e => handleFilterChange('category', e.target.value)}
                      displayEmpty
                      className="klass-filter-input"
                      IconComponent={KeyboardArrowDownIcon}
                      MenuProps={{
                        PaperProps: {
                          style: {
                            zIndex: 1500,
                          },
                        },
                      }}
                      sx={{
                        '& .MuiOutlinedInput-notchedOutline': {
                          border: 'none',
                        },
                      }}
                    >
                      {CategoryArray.map(opt => (
                        <MenuItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>

                {/* Years of Experience Filter */}
                <Box className="klass-filter-item">
                  <label className="klass-filter-label">Years of Experience</label>
                  <FormControl sx={{ width: '100%' }}>
                    <Select
                      value={pendingFilters.years}
                      onChange={e => handleFilterChange('years', e.target.value)}
                      displayEmpty
                      className="klass-filter-input"
                      IconComponent={KeyboardArrowDownIcon}
                      MenuProps={{
                        PaperProps: {
                          style: {
                            zIndex: 1500,
                          },
                        },
                      }}
                      sx={{
                        '& .MuiOutlinedInput-notchedOutline': {
                          border: 'none',
                        },
                      }}
                    >
                      {ExperienceArray.map(opt => (
                        <MenuItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>

                {/* Teaching Language Filter */}
                <Box className="klass-filter-item">
                  <label className="klass-filter-label">Teaching Language</label>
                  <FormControl sx={{ width: '100%' }}>
                    <Select
                      value={pendingFilters.language}
                      onChange={e => handleFilterChange('language', e.target.value)}
                      displayEmpty
                      className="klass-filter-input"
                      IconComponent={KeyboardArrowDownIcon}
                      MenuProps={{
                        PaperProps: {
                          className: 'klass-filter-menu-paper',
                          style: { zIndex: 2000, maxHeight: 240, overflowY: 'auto' },
                        },
                        anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
                        transformOrigin: { vertical: 'top', horizontal: 'left' },
                      }}
                      sx={{
                        '& .MuiOutlinedInput-notchedOutline': {
                          border: 'none',
                        },
                      }}
                    >
                      <MenuItem value="All">All</MenuItem>
                      {languages.map(lang => (
                        <MenuItem key={lang.id} value={lang.name}>
                          {lang.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>

                {/* Country Filter */}
                <Box className="klass-filter-item">
                  <label className="klass-filter-label">Country</label>
                  <FormControl sx={{ width: '100%' }}>
                    <Select
                      value={pendingFilters.country}
                      onChange={e => {
                        const name = e.target.value;
                        handleFilterChange('country', name);
                        // Resolve country id and load cities
                        const selected = countries.find(c => c.name === name);
                        const cId = selected?.id || null;
                        setSelectedCountryId(cId);
                        // Reset city when country changes
                        setPendingFilters(prev => ({ ...prev, city: 'All' }));
                        fetchCitiesForCountry(cId);
                      }}
                      displayEmpty
                      className="klass-filter-input"
                      IconComponent={KeyboardArrowDownIcon}
                      MenuProps={{
                        PaperProps: {
                          className: 'klass-filter-menu-paper',
                          style: { zIndex: 2000, maxHeight: 240, overflowY: 'auto' },
                        },
                        anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
                        transformOrigin: { vertical: 'top', horizontal: 'left' },
                      }}
                      sx={{
                        '& .MuiOutlinedInput-notchedOutline': {
                          border: 'none',
                        },
                      }}
                    >
                      <MenuItem value="All">All</MenuItem>
                      {countries.map(country => (
                        <MenuItem key={country.id} value={country.name}>
                          {country.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>

                {/* City Filter */}
                <Box className="klass-filter-item">
                  <label className="klass-filter-label">City</label>
                  <FormControl sx={{ width: '100%' }}>
                    <Select
                      value={pendingFilters.city}
                      onChange={e => handleFilterChange('city', e.target.value)}
                      displayEmpty
                      className="klass-filter-input"
                      IconComponent={KeyboardArrowDownIcon}
                      MenuProps={{
                        PaperProps: {
                          className: 'klass-filter-menu-paper',
                          style: { zIndex: 2000, maxHeight: 240, overflowY: 'auto' },
                        },
                        anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
                        transformOrigin: { vertical: 'top', horizontal: 'left' },
                      }}
                      sx={{
                        '& .MuiOutlinedInput-notchedOutline': {
                          border: 'none',
                        },
                      }}
                    >
                      <MenuItem value="All">All</MenuItem>
                      {cities.map(city => (
                        <MenuItem key={city.id} value={city.name}>
                          {city.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>

                {/* Gender Filter */}
                <Box className="klass-filter-item">
                  <label className="klass-filter-label">Gender</label>
                  <FormControl sx={{ width: '100%' }}>
                    <Select
                      value={pendingFilters.gender}
                      onChange={e => handleFilterChange('gender', e.target.value)}
                      displayEmpty
                      className="klass-filter-input"
                      IconComponent={KeyboardArrowDownIcon}
                      MenuProps={{
                        PaperProps: {
                          style: {
                            zIndex: 1500,
                          },
                        },
                      }}
                      sx={{
                        '& .MuiOutlinedInput-notchedOutline': {
                          border: 'none',
                        },
                      }}
                    >
                      {GenderArray.map(opt => (
                        <MenuItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              </Box>
            </Box>

            {/* Buttons (Sticky Footer) */}
            <Box className="klass-filter-buttons-container">
              <Button
                variant="outlined"
                onClick={handleClearAll}
                className="klass-filter-clear-button"
              >
                Clear All
              </Button>
              <Button
                variant="contained"
                onClick={handleApply}
                className="klass-filter-apply-button"
              >
                Apply
              </Button>
            </Box>
          </Popover>

          {/* Table */}
          <Box className="klass-add-instructor-page-table-container">
            {apiLoading ? (
              <Box className="klass-add-instructor-loading-container">
                <Typography>Loading instructors...</Typography>
              </Box>
            ) : error ? (
              <Box className="klass-add-instructor-error-container">
                <Typography color="error">{error}</Typography>
              </Box>
            ) : instructors.length === 0 ? (
              <Box className="klass-add-instructor-empty-container">
                <Typography>No instructor available</Typography>
              </Box>
            ) : (
              <TableContainer
                component={Paper}
                className="klass-add-instructor-table-paper"
                ref={tableContainerRef}
              >
                <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox">
                        <Checkbox
                          indeterminate={
                            selected.length > 0 && selected.length < instructors.length
                          }
                          checked={instructors.length > 0 && selected.length === instructors.length}
                          onChange={handleSelectAll}
                          inputProps={{ 'aria-label': 'select all instructors' }}
                        />
                      </TableCell>
                      <TableCell>Name</TableCell>
                      <TableCell>Instructor Type</TableCell>
                      <TableCell>Country</TableCell>
                      <TableCell>Subjects</TableCell>
                      <TableCell>Total Sessions</TableCell>
                      <TableCell>Rating</TableCell>
                      <TableCell>Type of session</TableCell>
                      <TableCell>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {instructors.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={9} align="center">
                          {loading ? 'Loading...' : 'No instructors found.'}
                        </TableCell>
                      </TableRow>
                    ) : (
                      instructors.map(row => (
                        <TableRow key={row.id} hover>
                          <TableCell padding="checkbox">
                            <Checkbox
                              checked={selected.includes(row.id)}
                              onChange={() => handleSelect(row.id)}
                              inputProps={{
                                'aria-label': `select instructor ${row.first_name} ${row.last_name}`,
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            <Box className="klass-add-instructor-avatar-name">
                              {row.profile_image ? (
                                <img
                                  src={row.profile_image}
                                  alt={`${row.first_name} ${row.last_name}`}
                                  className="klass-add-instructor-avatar"
                                />
                              ) : (
                                <div className="klass-add-instructor-avatar-placeholder" />
                              )}
                              <span>
                                {row.first_name} {row.last_name}
                              </span>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <span
                              className={`klass-instructor-type-badge ${row.instructor_type === 'Creative' ? 'klass-creative' : ''}`}
                            >
                              {row.instructor_type}
                            </span>
                          </TableCell>
                          <TableCell>
                            <Box className="klass-instructor-country-container">
                              {row.country && row.country !== 'N/A' ? (
                                <ReactCountryFlag
                                  countryCode={getCountryCode(row.country)}
                                  svg
                                  className="klass-country-flag"
                                  style={{ marginRight: 4 }}
                                />
                              ) : (
                                <span className="klass-country-flag">🌍</span>
                              )}
                              <span>{row.country}</span>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <PopoverSubjects subjects={row.subjects} />
                          </TableCell>
                          <TableCell>{row.total_sessions || 0}</TableCell>
                          <TableCell>{row.rating ? `${row.rating}/5` : 'N/A'}</TableCell>
                          <TableCell>
                            <Box className="klass-session-type-icons">
                              {row.allow_one_on_one_session && (
                                <img
                                  src={OneOnOneSessionIcon}
                                  alt="One-on-One"
                                  className="klass-session-icon-img"
                                />
                              )}
                              {row.allow_group_session && (
                                <img
                                  src={GroupSessionIcon}
                                  alt="Group"
                                  className="klass-session-icon-img"
                                />
                              )}
                              {row.allow_prime_session && (
                                <img
                                  src={PrimeSessionIcon}
                                  alt="Prime"
                                  className="klass-session-icon-img"
                                />
                              )}
                            </Box>
                          </TableCell>
                          <TableCell>
                            <span
                              className={`klass-status-badge ${row.is_active ? 'klass-enabled' : 'klass-disabled'}`}
                            >
                              {row.is_active ? 'Enabled' : 'Disabled'}
                            </span>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
            {loadingMore && (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 1 }}>
                <Typography>Loading more...</Typography>
              </Box>
            )}
          </Box>

          {/* Footer */}
          <Box className="klass-add-instructor-page-footer">
            <Typography className="klass-selected-count">{selected.length} Selected</Typography>
            <Box className="klass-footer-buttons">
              <Button
                onClick={() => navigate(`/admin/organisation/${orgId}`)}
                className="klass-add-instructor-cancel"
                variant="outlined"
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                onClick={handleConfirm}
                className="klass-add-instructor-confirm"
                variant="contained"
                disabled={selected.length === 0 || apiLoading || loading}
              >
                {loading ? 'Adding...' : 'Add'}
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>
    </Layout>
  );
};

// Helper function to get country code from country name
function getCountryCode(countryName) {
  if (!countryName || typeof countryName !== 'string') return undefined;

  const normalized = countryName.trim().toLowerCase();

  // Common aliases and alternate official names mapped to ISO Alpha-2
  const aliasMap = {
    usa: 'US',
    us: 'US',
    'united states': 'US',
    uk: 'GB',
    england: 'GB',
    'great britain': 'GB',
    britain: 'GB',
    'united kingdom': 'GB',
    uae: 'AE',
    'united arab emirates': 'AE',
    russia: 'RU',
    'russian federation': 'RU',
    'south korea': 'KR',
    'republic of korea': 'KR',
    'korea, republic of': 'KR',
    'korea (republic of)': 'KR',
    'north korea': 'KP',
    "korea, democratic people's republic of": 'KP',
    syria: 'SY',
    'syrian arab republic': 'SY',
    iran: 'IR',
    'iran, islamic republic of': 'IR',
    'islamic republic of iran': 'IR',
    palestine: 'PS',
    'state of palestine': 'PS',
    laos: 'LA',
    "lao people's democratic republic": 'LA',
    moldova: 'MD',
    'republic of moldova': 'MD',
    'czech republic': 'CZ',
    czechia: 'CZ',
    venezuela: 'VE',
    'bolivarian republic of venezuela': 'VE',
    'venezuela, bolivarian republic of': 'VE',
    bolivia: 'BO',
    'bolivia, plurinational state of': 'BO',
    tanzania: 'TZ',
    'tanzania, united republic of': 'TZ',
    'viet nam': 'VN',
    vietnam: 'VN',
    brunei: 'BN',
    'brunei darussalam': 'BN',
    myanmar: 'MM',
    burma: 'MM',
    macao: 'MO',
    macau: 'MO',
  };

  if (aliasMap[normalized]) return aliasMap[normalized];

  // Try library lookup with original and trimmed case
  const direct = getCountryCodeByName(countryName.trim());
  if (direct) return direct;

  // As a last attempt, return undefined to let caller render a fallback
  return undefined;
}

export default AddInstructor;
